import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Download, RefreshCw, Printer, Trash2, AlertTriangle, Lock, Box, Pencil, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../i18n";
import { apiFetch } from "../api/apiFetch";
import { useReadOnly } from "../access/ReadOnly";
import { useWarehouseScope } from "../access/useWarehouseScope";
import FilterPills from "./admin/FilterPills";
import { PALLET_COLOURS, PALLET_STATUSES, DEFAULT_SUPPLIER, isPortalStatus, isStorageColour, printPalletLabel } from "../pages/pallets";
import type { PalletRow, PalletColour, PalletStatus } from "../pages/pallets";
import "../styles/inbound-admin.css";
import "../styles/pallets.css";

// Admin › Warehouse › Pallets Created.
//   • warehouse pills (one table per warehouse); tiles that are the main
//     filters (Alex, 2026-10-15): Pallets (all) / Ready to list / Listed /
//     Needs resolving (Error + every Portal verdict) / Created today; colour
//     pills combine (any number at once; the list and both exports follow)
//     pills, a status pill row with the same choices plus Voided, search,
//     date range
//   • per row: tick box, SKU, colour (swatch only), type, size, supplier (hover for the
//     suppliers of the stock actually put away onto the pallet; click to
//     change it — a pop-up with every active supplier), where the pallet stands
//     (the storage location holding its own unit, by name; blank until
//     Put Away lands it), who printed ("Alex B"), the day it was made, what has been put away onto
//     it (units / SKUs / RRP value from inventory_detail +
//     products.rrp), notes, status, reprint, void
//   • statuses a person may choose: Ready to List (stored as not_listed),
//     Listing, Listed, Error - Needs Resolving. The Portal sets the rest from the
//     live stock before every list / export and nobody picks them: Mixed
//     Supplier (more than one supplier on the products), Different
//     Supplier (one supplier, not the label's), Empty (nothing on the
//     pallet; never a BLACK one). They show only as a row's current value
//     — and a choice made by hand STICKS: the BE records the dismissed
//     verdict and does not re-apply it, so an Empty or Mixed Supplier
//     pallet set to Ready to List stays there
//   • weight (kg) is worked out by the BE from the products on the pallet
//     (units × each product's weight) — nobody enters it. A warning mark
//     shows when some units have no product weight (the real weight is
//     higher) and the cell turns amber when the weight is over the
//     Palletways limit for the pallet's type (it may need booking as the
//     next size up)
//   • every row that needs resolving is red: Error (whatever is on it —
//     Alex, 2026-10-15), Mixed / Different Supplier and Empty. A Listed
//     pallet never is — it empties when its lot sells and the stock is
//     dispatched, which is the expected end of its life
//   • a Listed pallet's units / SKUs / values / weight are LOCKED by the
//     BE at the moment it was listed (migration 217) and shown with a
//     padlock; every other status shows the live sums. The lock lands on
//     the row from the PATCH response; the bulk path marks it locally and
//     the next reload shows the BE's truth
//   • bulk selector: tick rows (or the header box for every live row on
//     the page), pick a status, Apply → one POST for the lot. Selection
//     clears whenever the list reloads so a stale tick can't carry over
//   • Listing (Alex, 2026-10-22): tick the pallets you are about to list
//     and move them to Listing — the BE stamps your name and the batch is
//     yours: anyone else sees those rows locked (no tick box; status,
//     supplier and void disabled, the owner named on hover) unless they
//     are an Admin. Only Ready to List pallets can go into Listing; the
//     owner moves the batch to Listed (or back) from the My listing pill.
//     Listed by names who listed each pallet (the batch owner), and shows
//     whose batch it is while it sits in Listing
//   • paged, 50 a page by default (10 / 20 / 50 / 100, remembered per
//     browser), with the usual footer; the exports still take every row
//     the filters match
//   • the table borrows .locations-table (fixed layout) — pallets.css sets
//     this table's own column widths so Status never collides with the
//     action buttons
//   • XLSX export served by the BE with the same filters as the list, and
//     a second one, Stock Export: one line per pallet followed by one line
//     per product SKU on it (Alex, 2026-10-14)

// "listing_mine" = the viewer's own Listing batch (the BE matches on the
// session name).
type StatusFilter = "" | PalletStatus | "voided" | "needs_resolving" | "listing_mine";

// Who is looking, as the BE stamps it on a Listing batch, and whether
// they may release anyone's batch (Admin) — from the list response.
interface Me { name: string | null; admin: boolean }

// Palletways weight limit (kg) per pallet type.
export const PALLETWAYS_WEIGHT_LIMITS_KG: Record<string, number> = { MQP: 150, QP: 250, HP: 500, LP: 750, FP: 1200 };

const PAGE_SIZES = [10, 20, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_STORAGE_KEY = "pallets_page_size";
const readStoredPageSize = (): number => {
    try {
        const n = Number(window.localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
        return (PAGE_SIZES as readonly number[]).includes(n) ? n : DEFAULT_PAGE_SIZE;
    } catch { return DEFAULT_PAGE_SIZE; }
};

interface Summary {
    total: number;
    not_listed: number;
    listing?: number;
    listing_mine?: number;
    listed: number;
    error?: number;
    mixed_supplier?: number;
    different_supplier?: number;
    empty?: number;
    duplicate_sku?: number;
    needs_resolving?: number;
    today: number;
}
const NEEDS_RESOLVING: PalletStatus[] = ["error", "mixed_supplier", "different_supplier", "empty", "duplicate_sku"];

// Supplier pop-up anchored under the supplier cell: which pallet, what is
// picked so far, where to draw it.
interface SupplierEdit { sku: string; current: string; value: string; top: number; left: number }
// Notes pop-up anchored under the Notes cell (Alex, 2026-10-26): same
// shape, free text instead of a pick.
interface NotesEdit { sku: string; current: string; value: string; top: number; left: number }

const readUser = () => {
    try {
        const raw = window.localStorage.getItem("user");
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
};

const PalletsAdmin = () => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const warehouseScope = useWarehouseScope();
    const user = useMemo(readUser, []);

    const [warehouses, setWarehouses] = useState<string[]>([]);
    const [warehouse, setWarehouse] = useState<string>(user.warehouse || "");
    // Colours combine: an empty set is every colour (Alex, 2026-10-21).
    const [colourFilter, setColourFilter] = useState<string[]>([]);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
    const [todayOnly, setTodayOnly] = useState(false);
    const [q, setQ] = useState("");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState<number>(readStoredPageSize);

    const [rows, setRows] = useState<PalletRow[]>([]);
    const [total, setTotal] = useState(0);
    const [summary, setSummary] = useState<Summary | null>(null);
    const [me, setMe] = useState<Me | null>(null);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exportingStock, setExportingStock] = useState(false);
    const [busy, setBusy] = useState<string | null>(null);

    // Bulk selector — SKUs ticked in the table + the status to apply.
    const [selected, setSelected] = useState<Set<string>>(() => new Set());
    const [bulkStatus, setBulkStatus] = useState<PalletStatus | "">("");
    const [bulkApplying, setBulkApplying] = useState(false);

    // Supplier pop-up: every active supplier (same source as Goods In ›
    // Pallets), loaded once; the row's current supplier is always offered
    // even if it has since been archived.
    const [suppliers, setSuppliers] = useState<string[]>([DEFAULT_SUPPLIER]);
    const [supplierEdit, setSupplierEdit] = useState<SupplierEdit | null>(null);
    const [supplierSaving, setSupplierSaving] = useState(false);
    const supplierPopRef = useRef<HTMLDivElement | null>(null);

    // Notes pop-up: change or clear the note on a pallet from the table
    // (Alex, 2026-10-26). The BE stamps who edited it; the cell's hover
    // shows the name.
    const [notesEdit, setNotesEdit] = useState<NotesEdit | null>(null);
    const [notesSaving, setNotesSaving] = useState(false);
    const notesPopRef = useRef<HTMLDivElement | null>(null);

    // Notes pop-up. The cell truncates with an ellipsis; hovering (or
    // tabbing to) a truncated cell shows the whole note in a viewport-
    // positioned bubble — the table wrapper scrolls and clips cell
    // overflow, so an in-cell tooltip would be cut off at the edges.
    const [noteTip, setNoteTip] = useState<{ text: string; top: number; left: number; maxWidth: number } | null>(null);

    const hideNoteTip = () => setNoteTip(null);
    // The Supplier cell shows what has actually been put away onto the
    // pallet (Alex, 2026-10-16); the Notes cell the whole note and who last
    // edited it (Alex, 2026-10-26).
    const showCellTip = (el: HTMLElement, text: string | null | undefined) => {
        if (!text) return;
        const rect = el.getBoundingClientRect();
        const maxWidth = Math.min(420, window.innerWidth - 24);
        const left = Math.max(12, Math.min(rect.left, window.innerWidth - maxWidth - 12));
        setNoteTip({ text, top: rect.bottom + 6, left, maxWidth });
    };

    useEffect(() => {
        if (!noteTip) return;
        window.addEventListener("scroll", hideNoteTip, true);
        window.addEventListener("resize", hideNoteTip);
        return () => {
            window.removeEventListener("scroll", hideNoteTip, true);
            window.removeEventListener("resize", hideNoteTip);
        };
    }, [noteTip]);

    // The supplier pop-up is fixed-positioned under its cell, so it goes
    // when the page scrolls or resizes (it would drift otherwise), on
    // Escape, or on a click anywhere outside it.
    useEffect(() => {
        if (!supplierEdit) return;
        const close = () => setSupplierEdit(null);
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
        const onClick = (e: MouseEvent) => {
            if (supplierPopRef.current && !supplierPopRef.current.contains(e.target as Node)) close();
        };
        window.addEventListener("scroll", close, true);
        window.addEventListener("resize", close);
        window.addEventListener("keydown", onKey);
        window.addEventListener("mousedown", onClick);
        return () => {
            window.removeEventListener("scroll", close, true);
            window.removeEventListener("resize", close);
            window.removeEventListener("keydown", onKey);
            window.removeEventListener("mousedown", onClick);
        };
    }, [supplierEdit]);

    // The notes pop-up goes the same ways.
    useEffect(() => {
        if (!notesEdit) return;
        const close = () => setNotesEdit(null);
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
        const onClick = (e: MouseEvent) => {
            if (notesPopRef.current && !notesPopRef.current.contains(e.target as Node)) close();
        };
        window.addEventListener("scroll", close, true);
        window.addEventListener("resize", close);
        window.addEventListener("keydown", onKey);
        window.addEventListener("mousedown", onClick);
        return () => {
            window.removeEventListener("scroll", close, true);
            window.removeEventListener("resize", close);
            window.removeEventListener("keydown", onKey);
            window.removeEventListener("mousedown", onClick);
        };
    }, [notesEdit]);

    // Without cross_warehouse the page stays on the person's own warehouse.
    useEffect(() => {
        if (!warehouseScope.canCross && warehouseScope.own && warehouse !== warehouseScope.own) setWarehouse(warehouseScope.own);
    }, [warehouseScope.canCross, warehouseScope.own, warehouse]);

    // Warehouse list from /settings (admin), like the other warehouse pages.
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch(`/settings`, {});
                if (!res.ok) return;
                const data: { warehouse: string }[] = await res.json();
                const list = data.map(r => r.warehouse).sort();
                setWarehouses(list);
                setWarehouse(w => (w && list.includes(w) ? w : list[0] ?? ""));
            } catch { /* leave empty */ }
        })();
    }, []);

    // Supplier list for the pop-up: every active supplier, Amazon always in.
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch(`/suppliers?status=active&page=0&page_size=500`, {});
                if (!res.ok) return;
                const data = await res.json();
                const names: string[] = Array.isArray(data?.suppliers)
                    ? data.suppliers.map((s: { name?: string }) => s.name).filter((n: unknown): n is string => typeof n === "string" && n.trim() !== "")
                    : [];
                const list = Array.from(new Set([DEFAULT_SUPPLIER, ...names])).sort((a, b) => a.localeCompare(b));
                setSuppliers(list);
            } catch { /* keep the default */ }
        })();
    }, []);

    const buildQuery = useCallback((paged: boolean) => {
        const p = new URLSearchParams();
        p.set("warehouse", warehouse);
        if (colourFilter.length) p.set("colour", colourFilter.join(","));
        if (statusFilter) p.set("status", statusFilter);
        if (todayOnly) p.set("today", "1");
        if (q.trim()) p.set("q", q.trim());
        if (from) p.set("from", from);
        if (to) p.set("to", to);
        if (paged) {
            p.set("page", String(page));
            p.set("page_size", String(pageSize));
        }
        return p.toString();
    }, [warehouse, colourFilter, statusFilter, todayOnly, q, from, to, page, pageSize]);

    const load = useCallback(async () => {
        if (!warehouse) return;
        setLoading(true);
        try {
            const res = await apiFetch(`/pallets/admin/list?${buildQuery(true)}`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            const list: PalletRow[] = Array.isArray(data.rows) ? data.rows : [];
            setRows(list);
            setTotal(Number.isFinite(Number(data.total)) ? Number(data.total) : list.length);
            setSummary(data.summary ?? null);
            if (data.me) setMe({ name: data.me.name ?? null, admin: data.me.admin === true });
            setSelected(new Set());
            setSupplierEdit(null);
        } catch {
            toast.error(t("server_error"), { toastId: "pallets-admin-load" });
        } finally {
            setLoading(false);
        }
    }, [warehouse, buildQuery, t]);

    useEffect(() => {
        const handle = window.setTimeout(load, q ? 250 : 0);
        return () => window.clearTimeout(handle);
    }, [load, q]);

    // Any filter change starts again from page one. React batches the two
    // updates inside one event, so the list loads once.
    const changeWarehouse = (w: string) => { if (w) { setWarehouse(w); setPage(0); } };
    const changeColour = (c: string[]) => { setColourFilter(c); setPage(0); };
    const changeStatus = (s: StatusFilter) => { setStatusFilter(s); setPage(0); };
    const toggleToday = () => { setTodayOnly(v => !v); setPage(0); };
    const changeQ = (v: string) => { setQ(v); setPage(0); };
    const changeFrom = (v: string) => { setFrom(v); setPage(0); };
    const changeTo = (v: string) => { setTo(v); setPage(0); };
    const changePageSize = (n: number) => {
        setPageSize(n);
        setPage(0);
        try { window.localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(n)); } catch { /* ignore */ }
    };
    // The "Pallets" tile: everything (no status, not just today).
    const showAll = () => { setStatusFilter(""); setTodayOnly(false); setPage(0); };

    // Two XLSX downloads with the list's filters — every row they match,
    // not just this page: the page as shown (/export) and the Stock
    // Export (/stock-export) — one line per pallet followed by one line
    // per product SKU on it.
    const downloadXlsx = async (path: string, filename: string, setFlag: (on: boolean) => void) => {
        setFlag(true);
        try {
            const res = await apiFetch(`${path}?${buildQuery(false)}`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename.replace(/[^\w.-]/g, "_");
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast.error(t("server_error"), { toastId: "pallets-admin-export" });
        } finally {
            setFlag(false);
        }
    };
    const today = () => new Date().toISOString().slice(0, 10);
    const exportXlsx = () => downloadXlsx("/pallets/admin/export", `pallets-${warehouse}-${today()}.xlsx`, setExporting);
    const exportStock = () => downloadXlsx("/pallets/admin/stock-export", `pallet-stock-${warehouse}-${today()}.xlsx`, setExportingStock);

    // Whose Listing batch a row is in, if not the viewer's: the lock
    // (Alex, 2026-10-22). An Admin may change anyone's batch. Until the
    // BE has said who is asking, every Listing row counts as locked.
    const isMine = (r: PalletRow) => me != null && (r.listing_by_name ?? null) === (me.name ?? null);
    const lockedByOther = (r: PalletRow) => r.status === "listing" && !me?.admin && !isMine(r);

    // Tile counters follow a status change without a round trip; Needs
    // resolving is the sum of its parts, so it is re-added from them. My
    // listing: everything going INTO Listing is the viewer's; `mine` says
    // how many of the rows leaving it were theirs.
    const bumpSummary = (from: PalletStatus, to: PalletStatus, n = 1, mine = 0) =>
        setSummary(s => {
            if (!s || from === to) return s;
            const next: Summary = { ...s, [from]: Math.max(0, (s[from] ?? 0) - n), [to]: (s[to] ?? 0) + n };
            next.needs_resolving = NEEDS_RESOLVING.reduce((sum, k) => sum + (next[k] ?? 0), 0);
            if (to === "listing") next.listing_mine = (s.listing_mine ?? 0) + n;
            if (from === "listing") next.listing_mine = Math.max(0, (s.listing_mine ?? 0) - mine);
            return next;
        });

    // The BE's refusals worth their own words: a pallet in someone else's
    // Listing batch, and one that is not Ready to List going into Listing.
    const statusRefusalToast = (sku: string, data: { reason?: string; by?: string | null } | null, toastId: string) => {
        if (data?.reason === "locked") toast.error(t("pallets_admin_listing_locked_toast", { sku, name: data.by ?? "" }), { toastId });
        else if (data?.reason === "not_ready") toast.error(t("pallets_admin_listing_not_ready", { sku }), { toastId });
        else toast.error(t("server_error"), { toastId });
    };

    const setStatus = async (r: PalletRow, status: PalletStatus) => {
        if (status === r.status) return;
        setBusy(r.pallet_sku);
        try {
            const res = await apiFetch(`/pallets/admin/${encodeURIComponent(r.pallet_sku)}/status`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ status }),
            });
            const data = await res.json().catch(() => null);
            if (!res.ok || !data?.success) {
                statusRefusalToast(r.pallet_sku, data, "pallets-admin-status");
                return;
            }
            setRows(curr => curr.map(x => (x.pallet_sku === r.pallet_sku ? { ...x, ...(data.pallet ?? { status }) } : x)));
            bumpSummary(r.status, status, 1, r.status === "listing" && isMine(r) ? 1 : 0);
            toast.success(t("pallets_admin_status_saved", { sku: r.pallet_sku, status: t(`pallets_status_${status}`) }), { toastId: "pallets-admin-status" });
        } catch {
            toast.error(t("server_error"), { toastId: "pallets-admin-status" });
        } finally {
            setBusy(null);
        }
    };

    // Supplier pop-up: open under the cell, pick, save. The BE judges the
    // pallet afresh against the new supplier, so the row that comes back
    // may carry a new status (Different Supplier → Ready to List).
    const openSupplierEdit = (el: HTMLElement, r: PalletRow) => {
        if (readOnly) return;
        hideNoteTip();
        const rect = el.getBoundingClientRect();
        const width = 260;
        const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));
        const current = r.supplier_name ?? DEFAULT_SUPPLIER;
        setSupplierEdit({ sku: r.pallet_sku, current, value: current, top: rect.bottom + 4, left });
    };

    const saveSupplier = async () => {
        if (!supplierEdit) return;
        const row = rows.find(x => x.pallet_sku === supplierEdit.sku);
        const supplierName = supplierEdit.value.trim();
        if (!row || !supplierName) return;
        if (supplierName === (row.supplier_name ?? "")) { setSupplierEdit(null); return; }
        setSupplierSaving(true);
        try {
            const res = await apiFetch(`/pallets/admin/${encodeURIComponent(row.pallet_sku)}/supplier`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ supplierName }),
            });
            const data = await res.json().catch(() => null);
            if (!res.ok || !data?.success) {
                statusRefusalToast(row.pallet_sku, data, "pallets-admin-supplier");
                return;
            }
            const next: PalletRow = { ...row, ...(data.pallet ?? { supplier_name: supplierName }) };
            setRows(curr => curr.map(x => (x.pallet_sku === row.pallet_sku ? next : x)));
            if (next.status !== row.status) bumpSummary(row.status, next.status);
            setSupplierEdit(null);
            toast.success(t("pallets_admin_supplier_saved", { sku: row.pallet_sku, supplier: next.supplier_name ?? supplierName }), { toastId: "pallets-admin-supplier" });
        } catch {
            toast.error(t("server_error"), { toastId: "pallets-admin-supplier" });
        } finally {
            setSupplierSaving(false);
        }
    };

    const openNotesEdit = (el: HTMLElement, r: PalletRow) => {
        if (readOnly) return;
        hideNoteTip();
        const rect = el.getBoundingClientRect();
        const width = 320;
        const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));
        const current = r.notes ?? "";
        setNotesEdit({ sku: r.pallet_sku, current, value: current, top: rect.bottom + 4, left });
    };

    const saveNotes = async () => {
        if (!notesEdit) return;
        const row = rows.find(x => x.pallet_sku === notesEdit.sku);
        if (!row) return;
        const notes = notesEdit.value.trim();
        if (notes === (row.notes ?? "").trim()) { setNotesEdit(null); return; }
        setNotesSaving(true);
        try {
            const res = await apiFetch(`/pallets/admin/${encodeURIComponent(row.pallet_sku)}/notes`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ notes }),
            });
            const data = await res.json().catch(() => null);
            if (!res.ok || !data?.success) {
                statusRefusalToast(row.pallet_sku, data, "pallets-admin-notes");
                return;
            }
            const next: PalletRow = { ...row, ...(data.pallet ?? { notes: notes || null }) };
            setRows(curr => curr.map(x => (x.pallet_sku === row.pallet_sku ? next : x)));
            setNotesEdit(null);
            toast.success(t("pallets_admin_notes_saved", { sku: row.pallet_sku }), { toastId: "pallets-admin-notes" });
        } catch {
            toast.error(t("server_error"), { toastId: "pallets-admin-notes" });
        } finally {
            setNotesSaving(false);
        }
    };

    // Hover / focus on the Notes cell: the whole note, then who last edited
    // it and when (Alex, 2026-10-26). Nothing when there is neither.
    const notesTipFor = (r: PalletRow) => {
        const lines: string[] = [];
        if (r.notes) lines.push(r.notes);
        if (r.notes_updated_by_name) {
            lines.push(t("pallets_admin_notes_edited_by", {
                name: r.notes_updated_by_name,
                when: r.notes_updated_at ? fmtWhen(r.notes_updated_at) : "",
            }).trim());
        }
        return lines.length ? lines.join("\n") : null;
    };

    // Rows the bulk selector can act on: live (not voided) rows on the page
    // that are not in someone else's Listing batch.
    const selectable = rows.filter(r => !r.voided_at && !isStorageColour(r.colour) && !lockedByOther(r));
    const allSelected = selectable.length > 0 && selectable.every(r => selected.has(r.pallet_sku));

    const toggleOne = (sku: string, on: boolean) =>
        setSelected(curr => {
            const next = new Set(curr);
            if (on) next.add(sku); else next.delete(sku);
            return next;
        });
    const toggleAll = (on: boolean) =>
        setSelected(on ? new Set(selectable.map(r => r.pallet_sku)) : new Set());

    const applyBulkStatus = async () => {
        if (selected.size === 0 || !bulkStatus) return;
        const picked = selectable.filter(r => selected.has(r.pallet_sku));
        // Into Listing only from Ready to List: anything else ticked is
        // left out here and said so, rather than sent for the BE to skip.
        const ready = bulkStatus === "listing" ? picked.filter(r => r.status === "not_listed") : picked;
        const notReady = picked.length - ready.length;
        const skus = ready.map(r => r.pallet_sku);
        if (notReady > 0) toast.warn(t("pallets_admin_bulk_not_ready", { n: String(notReady) }), { toastId: "pallets-admin-bulk-not-ready" });
        if (skus.length === 0) return;
        setBulkApplying(true);
        try {
            const res = await apiFetch(`/pallets/admin/status/bulk`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ skus, status: bulkStatus }),
            });
            const data = await res.json().catch(() => null);
            if (!res.ok || !data?.success) throw new Error(`HTTP ${res.status}`);
            const updated = new Set<string>(Array.isArray(data.updated) ? data.updated : skus);
            const status = bulkStatus;
            // Tally from the rendered rows (not inside the setRows updater,
            // which StrictMode double-invokes in development).
            const byStatus: Record<string, number> = {};
            let mine = 0;
            for (const x of rows) {
                if (updated.has(x.pallet_sku) && x.status !== status) {
                    byStatus[x.status] = (byStatus[x.status] ?? 0) + 1;
                    if (x.status === "listing" && isMine(x)) mine += 1;
                }
            }
            // Listed locks the figures (the BE snapshots them); off Listed
            // the live sums show again. Figures stay as they are until the
            // next reload — at the moment of listing they are the same.
            // The two names follow the BE's rule: the viewer owns what goes
            // into Listing; Listed by is the batch owner (or the viewer,
            // straight from Ready to List) and goes when the pallet leaves
            // Listed.
            const myName = me?.name ?? null;
            setRows(curr => curr.map(x => (updated.has(x.pallet_sku)
                ? {
                    ...x,
                    status,
                    contents_locked_at: status === "listed" ? (x.contents_locked_at ?? new Date().toISOString()) : null,
                    listing_by_name: status === "listing" ? myName : null,
                    listed_by_name: status !== "listed" ? null
                        : x.status === "listed" ? (x.listed_by_name ?? null)
                        : x.status === "listing" ? (x.listing_by_name ?? myName)
                        : myName,
                }
                : x)));
            for (const [from, n] of Object.entries(byStatus)) bumpSummary(from as PalletStatus, status, n, from === "listing" ? mine : 0);
            setSelected(new Set());
            setBulkStatus("");
            toast.success(t("pallets_admin_bulk_saved", { n: String(updated.size), status: t(`pallets_status_${status}`) }), { toastId: "pallets-admin-bulk" });
            const skipped: string[] = Array.isArray(data.skipped) ? data.skipped : [];
            if (skipped.length > 0) {
                toast.warn(t("pallets_admin_bulk_skipped", { n: String(skipped.length) }), { toastId: "pallets-admin-bulk-skipped" });
                await load();
            }
        } catch {
            toast.error(t("server_error"), { toastId: "pallets-admin-bulk" });
        } finally {
            setBulkApplying(false);
        }
    };

    const reprint = async (r: PalletRow) => {
        setBusy(r.pallet_sku);
        try {
            await apiFetch(`/pallets/${encodeURIComponent(r.pallet_sku)}/reprint`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({}),
            });
            const ok = await printPalletLabel(r);
            if (ok) toast.success(t("pallets_admin_reprinted"), { toastId: "pallets-admin-reprint" });
            else toast.error(t("print_failed"), { toastId: "pallets-admin-reprint" });
        } finally {
            setBusy(null);
        }
    };

    const voidPallet = async (r: PalletRow) => {
        if (!window.confirm(t("pallets_admin_void_confirm", { sku: r.pallet_sku }))) return;
        setBusy(r.pallet_sku);
        try {
            const res = await apiFetch(`/pallets/admin/${encodeURIComponent(r.pallet_sku)}/void`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({}),
            });
            const data = await res.json().catch(() => null);
            if (!res.ok || !data?.success) {
                if (data?.reason === "has_stock") {
                    toast.error(t("pallets_admin_void_has_stock", { sku: r.pallet_sku, units: String(data.units ?? "") }), { toastId: "pallets-admin-void" });
                } else {
                    statusRefusalToast(r.pallet_sku, data, "pallets-admin-void");
                }
                return;
            }
            toast.info(t("pallets_admin_voided", { sku: r.pallet_sku }), { toastId: "pallets-admin-void" });
            await load();
        } catch {
            toast.error(t("server_error"), { toastId: "pallets-admin-void" });
        } finally {
            setBusy(null);
        }
    };

    const colourMeta = (key: PalletColour) => PALLET_COLOURS.find(c => c.key === key);
    const colourOptions = PALLET_COLOURS.map(c => ({ value: c.key, label: t(c.labelKey) }));
    // The same choices as the tiles, then each reason under Needs resolving
    // on its own (Alex, 2026-10-21), plus Voided (which has no tile).
    const statusOptions = [
        { value: "not_listed", label: t("pallets_status_not_listed"), count: summary?.not_listed },
        { value: "listing", label: t("pallets_status_listing"), count: summary?.listing },
        { value: "listing_mine", label: t("pallets_status_listing_mine"), count: summary?.listing_mine },
        { value: "listed", label: t("pallets_status_listed"), count: summary?.listed },
        { value: "needs_resolving", label: t("pallets_status_needs_resolving"), count: summary?.needs_resolving },
        ...NEEDS_RESOLVING.map(s => ({ value: s, label: t(`pallets_status_${s}`), count: summary?.[s] })),
        { value: "voided", label: t("pallets_status_voided") },
    ];

    const fmtWhen = (iso: string) => {
        const d = new Date(iso);
        return `${d.toLocaleDateString("en-GB")} ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
    };
    // Date made shows the day only (Alex, 2026-10-21); the time is still
    // in the XLSX export and the cell's hover.
    const fmtDay = (iso: string) => new Date(iso).toLocaleDateString("en-GB");
    // Printed by: "Alex Bayley" reads "Alex B" (Alex, 2026-10-21); a
    // one-word name ("Desk") stays as it is. The full name is on hover.
    const shortName = (name: string) => {
        const parts = name.trim().split(/\s+/);
        return parts.length > 1 ? `${parts[0]} ${parts[1].charAt(0)}` : parts[0];
    };
    const money = (n: number) => n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.min(page, totalPages - 1);
    const rangeFrom = total === 0 ? 0 : safePage * pageSize + 1;
    const rangeTo = Math.min((safePage + 1) * pageSize, total);
    const allActive = statusFilter === "" && !todayOnly;
    const tileClass = (active: boolean) => `inbound-admin-tile${active ? " inbound-admin-tile--active" : ""}`;

    // The pop-up's choices: the list, plus the row's own supplier if it is
    // not in it (archived since the label was printed).
    const supplierChoices = supplierEdit && !suppliers.includes(supplierEdit.current)
        ? [supplierEdit.current, ...suppliers]
        : suppliers;

    return (
        <div className="pallets-admin">
            <FilterPills
                className="warehouse-admin-pills inbound-admin-warehouse-pills"
                value={warehouse}
                onChange={changeWarehouse}
                options={warehouseScope.limit(warehouses).map(w => ({ value: w, label: w }))}
                hideAll={!warehouseScope.canCross}
                allLabelKey="warehouse_col"
            />

            <div className="inbound-admin-tiles">
                <button type="button" className={tileClass(allActive)} data-testid="pallets-tile-total" onClick={showAll}>
                    <span className="inbound-admin-tile-num">{summary?.total ?? "—"}</span>
                    <span className="inbound-admin-tile-label">{t("pallets_admin_tile_total")}</span>
                </button>
                <button type="button" className={tileClass(statusFilter === "not_listed")} data-testid="pallets-tile-not_listed" onClick={() => changeStatus(statusFilter === "not_listed" ? "" : "not_listed")}>
                    <span className="inbound-admin-tile-num">{summary?.not_listed ?? "—"}</span>
                    <span className="inbound-admin-tile-label">{t("pallets_admin_tile_not_listed")}</span>
                </button>
                <button type="button" className={tileClass(statusFilter === "listing" || statusFilter === "listing_mine")} data-testid="pallets-tile-listing" title={t("pallets_admin_tile_listing_hint")} onClick={() => changeStatus(statusFilter === "listing" ? "" : "listing")}>
                    <span className="inbound-admin-tile-num">{summary?.listing ?? "—"}</span>
                    <span className="inbound-admin-tile-label">{t("pallets_admin_tile_listing")}</span>
                </button>
                <button type="button" className={tileClass(statusFilter === "listed")} data-testid="pallets-tile-listed" onClick={() => changeStatus(statusFilter === "listed" ? "" : "listed")}>
                    <span className="inbound-admin-tile-num">{summary?.listed ?? "—"}</span>
                    <span className="inbound-admin-tile-label">{t("pallets_admin_tile_listed")}</span>
                </button>
                <button type="button" className={tileClass(statusFilter === "needs_resolving")} data-testid="pallets-tile-needs_resolving" title={t("pallets_admin_tile_needs_resolving_hint")} onClick={() => changeStatus(statusFilter === "needs_resolving" ? "" : "needs_resolving")}>
                    <span className="inbound-admin-tile-num">{summary?.needs_resolving ?? "—"}</span>
                    <span className="inbound-admin-tile-label">{t("pallets_admin_tile_needs_resolving")}</span>
                </button>
                <button type="button" className={tileClass(todayOnly)} data-testid="pallets-tile-today" onClick={toggleToday}>
                    <span className="inbound-admin-tile-num">{summary?.today ?? "—"}</span>
                    <span className="inbound-admin-tile-label">{t("pallets_admin_tile_today")}</span>
                </button>
            </div>

            <FilterPills
                className="warehouse-admin-pills"
                selected={colourFilter}
                onSelectedChange={changeColour}
                options={colourOptions}
                labelKey="pallets_admin_col_colour"
            />
            <FilterPills
                className="warehouse-admin-pills"
                value={statusFilter}
                onChange={v => changeStatus(v as StatusFilter)}
                options={statusOptions}
                labelKey="pallets_admin_col_status"
                trailing={(
                    <div className="inbound-admin-actions">
                        <label className="pallets-admin-dates">
                            <span>{t("pallets_admin_from")}</span>
                            <input type="date" value={from} onChange={e => changeFrom(e.target.value)} aria-label={t("pallets_admin_from")} />
                        </label>
                        <label className="pallets-admin-dates">
                            <span>{t("pallets_admin_to")}</span>
                            <input type="date" value={to} onChange={e => changeTo(e.target.value)} aria-label={t("pallets_admin_to")} />
                        </label>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={exportXlsx} disabled={exporting || total === 0}>
                            <Download size={14} /> {exporting ? t("inventory_exporting") : t("pallets_admin_export")}
                        </button>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={exportStock} disabled={exportingStock || total === 0} title={t("pallets_admin_stock_export_hint")}>
                            <Download size={14} /> {exportingStock ? t("inventory_exporting") : t("pallets_admin_stock_export")}
                        </button>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={load} disabled={loading}>
                            <RefreshCw size={14} /> {t("refresh")}
                        </button>
                    </div>
                )}
            />

            <div className="warehouse-admin-filters users-admin-filters">
                <input
                    className="locations-table-search wh-admin-filter-search"
                    type="search"
                    placeholder={t("pallets_admin_search_hint")}
                    aria-label={t("pallets_admin_search_hint")}
                    value={q}
                    onChange={e => changeQ(e.target.value)}
                />
                <div className="inbound-admin-actions pallets-admin-bulk">
                    {selected.size > 0 && (
                        <span className="pallets-admin-bulk-count">{t("selected_count", { n: String(selected.size) })}</span>
                    )}
                    <select
                        className="pallets-admin-bulk-select"
                        value={bulkStatus}
                        aria-label={t("change_status_to")}
                        title={readOnly ? t("read_only_mode") : undefined}
                        onChange={e => setBulkStatus(e.target.value as PalletStatus | "")}
                        disabled={selected.size === 0 || bulkApplying || readOnly}
                    >
                        <option value="">{t("change_status_to")}</option>
                        {PALLET_STATUSES.map(s => (
                            <option key={s} value={s}>{t(`pallets_status_${s}`)}</option>
                        ))}
                    </select>
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={applyBulkStatus}
                        title={readOnly ? t("read_only_mode") : undefined}
                        disabled={selected.size === 0 || !bulkStatus || bulkApplying || readOnly}
                    >
                        {bulkApplying ? t("submitting") : t("apply")}
                    </button>
                </div>
            </div>

            <div className="locations-table-wrap">
                {rows.length === 0 ? (
                    <div className="locations-table-empty">{loading ? t("loading") : t("pallets_admin_none")}</div>
                ) : (
                    <table className="locations-table inbound-admin-table pallets-admin-table">
                        <thead>
                            <tr>
                                <th className="pallets-admin-check">
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        disabled={selectable.length === 0 || bulkApplying}
                                        aria-label={t("pallets_admin_select_all")}
                                        onChange={e => toggleAll(e.target.checked)}
                                    />
                                </th>
                                <th>{t("pallets_admin_col_sku")}</th>
                                <th>{t("pallets_admin_col_colour")}</th>
                                <th>{t("pallets_admin_col_type")}</th>
                                <th>{t("pallets_admin_col_size")}</th>
                                <th>{t("pallets_admin_col_supplier")}</th>
                                <th>{t("pallets_admin_col_location")}</th>
                                <th>{t("pallets_admin_col_by")}</th>
                                <th>{t("pallets_admin_col_date")}</th>
                                <th className="pallets-admin-num">{t("pallets_admin_col_units")}</th>
                                <th className="pallets-admin-num">{t("pallets_admin_col_skus")}</th>
                                <th className="pallets-admin-num">{t("pallets_admin_col_rrp")}</th>
                                <th className="pallets-admin-num" title={t("pallets_admin_weight_hint")}>{t("pallets_admin_col_weight")}</th>
                                <th>{t("pallets_admin_col_notes")}</th>
                                <th>{t("pallets_admin_col_status")}</th>
                                <th>{t("pallets_admin_col_listed_by")}</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => {
                                const voided = !!r.voided_at;
                                // Red rows: everything under Needs resolving — an
                                // Error pallet whatever is on it, and the Portal's
                                // verdicts (the wrong supplier, or several; nothing
                                // on the pallet; a SKU that is also on another
                                // pallet). The BE never calls a BLACK pallet
                                // empty (unmanifested stock, no SKUs), nor a Listed
                                // one — it empties when its lot sells, which is the
                                // expected end of its life.
                                const error = !voided && r.status === "error";
                                const mixed = !voided && (r.status === "mixed_supplier" || r.status === "different_supplier");
                                const empty = !voided && r.status === "empty";
                                const duplicate = !voided && r.status === "duplicate_sku";
                                const meta = colourMeta(r.colour);
                                const rowClass = voided ? "pallets-admin-row--voided"
                                    : error ? "pallets-admin-row--error"
                                    : duplicate ? "pallets-admin-row--duplicate"
                                    : mixed ? "pallets-admin-row--mixed"
                                    : empty ? "pallets-admin-row--empty"
                                    : undefined;
                                // Duplicate SKU (Alex, 2026-10-28): the other pallet(s)
                                // are named under the status — "Also on PD-RED7" — and
                                // the hover says which SKU is on which. The BE only
                                // sends the detail while its verdict stands; a Listed
                                // pallet on the other side keeps its status and shows
                                // nothing here.
                                const duplicates = voided ? [] : (r.duplicate_skus ?? []);
                                const duplicatePallets = Array.from(new Set(duplicates.flatMap(d => d.pallets))).sort();
                                const duplicateNote = duplicates.length
                                    ? [t("pallets_admin_duplicate_hint"), ...duplicates.map(d => t("pallets_admin_duplicate_sku_line", { sku: d.sku, pallets: d.pallets.join(", ") }))].join("\n")
                                    : "";
                                // Products that disagree with the label's supplier: named
                                // beside the supplier on every such row, whatever its status.
                                const supplierNote = r.supplier_mismatch
                                    ? t("pallets_admin_supplier_mismatch", { suppliers: (r.product_suppliers ?? []).join(", ") })
                                    : "";
                                // Hover / focus bubble on the Supplier cell: the label's
                                // supplier against the suppliers of the stock put away
                                // onto the pallet, plus the mismatch note when they differ.
                                const productSuppliers = r.product_suppliers ?? [];
                                const supplierTip = [
                                    t("pallets_admin_supplier_on_label", { supplier: r.supplier_name || "—" }),
                                    productSuppliers.length
                                        ? t("pallets_admin_supplier_on_pallet", { suppliers: productSuppliers.join(", ") })
                                        : t("pallets_admin_supplier_on_pallet_none"),
                                    supplierNote,
                                ].filter(Boolean).join("\n");
                                // The dropdown offers what a person may choose (Mixed /
                                // Different Supplier included since 2026-10-21); Empty
                                // appears only as the row's current value.
                                // Into Listing only from Ready to List: Listing is offered on
                                // a Ready to List row (and is the current value on a Listing one).
                                const statusChoices = (isPortalStatus(r.status) && !PALLET_STATUSES.includes(r.status) ? [r.status, ...PALLET_STATUSES] : PALLET_STATUSES)
                                    .filter(s => s !== "listing" || r.status === "not_listed" || r.status === "listing");
                                // A storage pallet (DSD) is Listed from creation and stays
                                // so: no tick box for the bulk selector, no dropdown.
                                const storage = isStorageColour(r.colour);
                                // In someone else's Listing batch: no tick box, and the
                                // status, supplier and void controls are theirs (or an Admin's).
                                const locked = !voided && lockedByOther(r);
                                const batchNote = locked ? t("pallets_admin_listing_locked", { name: r.listing_by_name ?? "" }) : "";
                                const statusNote = readOnly ? t("read_only_mode")
                                    : locked ? batchNote
                                    : duplicate && duplicateNote ? duplicateNote
                                    : r.status === "empty" ? t("pallets_admin_empty_hint")
                                    : mixed && supplierNote ? supplierNote
                                    : undefined;
                                // Weight from the pallet's contents, in kg to
                                // one decimal. Two things are worth a mark:
                                // units with no product weight (the figure is
                                // short), and a weight over the Palletways
                                // limit for this pallet's type.
                                const weightKg = r.weight_grams != null ? (r.weight_grams / 1000).toFixed(1) : null;
                                const unweighed = r.unweighed_units ?? 0;
                                const limitKg = r.palletways_type ? PALLETWAYS_WEIGHT_LIMITS_KG[r.palletways_type] : undefined;
                                const overLimit = r.weight_grams != null && limitKg !== undefined && r.weight_grams > limitKg * 1000;
                                // A volumetric warehouse's figure is footprint × height, not
                                // the contents: say so, with the sum, beside the weight.
                                const volumetric = r.weight_basis === "volumetric";
                                const weightNotes = [
                                    overLimit ? t("pallets_admin_weight_over_limit", { weight: weightKg ?? "", type: r.palletways_type ?? "", limit: String(limitKg) }) : "",
                                    unweighed > 0 ? t("pallets_admin_weight_partial", { n: String(unweighed) }) : "",
                                ].filter(Boolean).join(" · ");
                                const volumetricNote = volumetric ? t("pallets_admin_weight_volumetric", { formula: r.weight_formula ?? "" }) : "";
                                const lockedNote = r.contents_locked_at ? t("pallets_admin_locked", { date: fmtWhen(r.contents_locked_at) }) : "";
                                return (
                                    <tr key={r.pallet_sku} className={rowClass} data-testid={`pallet-row-${r.pallet_sku}`}>
                                        <td className="pallets-admin-check">
                                            {!voided && !storage && !locked && (
                                                <input
                                                    type="checkbox"
                                                    checked={selected.has(r.pallet_sku)}
                                                    disabled={bulkApplying}
                                                    aria-label={`${t("pallets_admin_select")} ${r.pallet_sku}`}
                                                    onChange={e => toggleOne(r.pallet_sku, e.target.checked)}
                                                />
                                            )}
                                        </td>
                                        <td><strong>{r.pallet_sku}</strong></td>
                                        <td className="pallets-admin-colour-cell">
                                            <span
                                                className="pallets-swatch"
                                                style={{ background: meta?.swatch }}
                                                role="img"
                                                aria-label={t(meta?.labelKey ?? "")}
                                                title={t(meta?.labelKey ?? "")}
                                                data-testid={`pallet-colour-${r.pallet_sku}`}
                                            />
                                        </td>
                                        <td>{r.pallet_type ?? "—"}</td>
                                        <td>{r.size_label || "—"}</td>
                                        <td
                                            className="pallets-admin-supplier-cell"
                                            data-testid={`pallet-supplier-${r.pallet_sku}`}
                                            onMouseEnter={e => showCellTip(e.currentTarget, supplierTip)}
                                            onMouseLeave={hideNoteTip}
                                            onFocus={e => showCellTip(e.currentTarget, supplierTip)}
                                            onBlur={hideNoteTip}
                                        >
                                            {voided ? (
                                                <span>{r.supplier_name || "—"}</span>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="pallets-admin-supplier-btn"
                                                    disabled={readOnly || busy === r.pallet_sku || locked}
                                                    title={locked ? batchNote : undefined}
                                                    aria-label={`${t("pallets_admin_change_supplier")} ${r.pallet_sku}`}
                                                    onClick={e => openSupplierEdit(e.currentTarget, r)}
                                                >
                                                    <span className="pallets-admin-supplier-name">{r.supplier_name || "—"}</span>
                                                    <Pencil size={11} aria-hidden="true" />
                                                </button>
                                            )}
                                            {supplierNote && (
                                                <span className="pallets-admin-supplier-flag" role="img" aria-label={supplierNote} data-testid={`pallet-supplier-flag-${r.pallet_sku}`}>
                                                    <AlertTriangle size={12} />
                                                </span>
                                            )}
                                        </td>
                                        <td title={r.location_barcode && r.location_barcode !== r.location_name ? r.location_barcode : undefined} data-testid={`pallet-location-${r.pallet_sku}`}>
                                            {r.location_name || "—"}
                                        </td>
                                        <td title={r.created_by_name ?? undefined}>{r.created_by_name ? shortName(r.created_by_name) : "—"}</td>
                                        <td title={fmtWhen(r.created_at)}>{fmtDay(r.created_at)}</td>
                                        <td className="pallets-admin-num" data-testid={`pallet-units-${r.pallet_sku}`}>
                                            {lockedNote && (
                                                <span className="pallets-admin-lock" role="img" aria-label={lockedNote} title={lockedNote}>
                                                    <Lock size={12} />
                                                </span>
                                            )}
                                            {r.units ?? 0}
                                        </td>
                                        <td className="pallets-admin-num">{r.skus ?? 0}</td>
                                        <td className="pallets-admin-num">{money(r.rrp_value ?? 0)}</td>
                                        <td
                                            className={`pallets-admin-num pallets-admin-weight${overLimit ? " pallets-admin-weight--over" : ""}`}
                                            data-testid={`pallet-weight-${r.pallet_sku}`}
                                        >
                                            {weightKg ?? "—"}
                                            {volumetricNote && (
                                                <span className="pallets-admin-weight-vol" role="img" aria-label={volumetricNote} title={volumetricNote}>
                                                    <Box size={12} />
                                                </span>
                                            )}
                                            {weightNotes && (
                                                <span className="pallets-admin-weight-flag" role="img" aria-label={weightNotes} title={weightNotes}>
                                                    <AlertTriangle size={12} />
                                                </span>
                                            )}
                                        </td>
                                        <td
                                            className="pallets-admin-notes"
                                            data-testid={`pallet-notes-${r.pallet_sku}`}
                                            tabIndex={voided && r.notes ? 0 : undefined}
                                            onMouseEnter={e => showCellTip(e.currentTarget, notesTipFor(r))}
                                            onMouseLeave={hideNoteTip}
                                            onFocus={e => showCellTip(e.currentTarget, notesTipFor(r))}
                                            onBlur={hideNoteTip}
                                        >
                                            {voided ? (
                                                <span>{r.notes || "—"}</span>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="pallets-admin-supplier-btn pallets-admin-notes-btn"
                                                    disabled={readOnly || busy === r.pallet_sku}
                                                    aria-label={`${t("pallets_admin_edit_notes")} ${r.pallet_sku}`}
                                                    onClick={e => openNotesEdit(e.currentTarget, r)}
                                                >
                                                    <span className="pallets-admin-supplier-name">{r.notes || "—"}</span>
                                                    <Pencil size={11} aria-hidden="true" />
                                                </button>
                                            )}
                                        </td>
                                        <td>
                                            {voided ? (
                                                <span>{t("pallets_status_voided")}</span>
                                            ) : storage ? (
                                                <span
                                                    className={`pallets-admin-status pallets-admin-status--${r.status} pallets-admin-status--fixed`}
                                                    title={t("pallets_admin_dsd_hint")}
                                                    aria-label={`${t("pallets_admin_col_status")} ${r.pallet_sku}`}
                                                >
                                                    {t(`pallets_status_${r.status}`)}
                                                </span>
                                            ) : (
                                                <select
                                                    className={`pallets-admin-status pallets-admin-status--${r.status}`}
                                                    value={r.status}
                                                    disabled={busy === r.pallet_sku || readOnly || locked}
                                                    aria-disabled={readOnly || locked}
                                                    aria-label={`${t("pallets_admin_col_status")} ${r.pallet_sku}`}
                                                    title={statusNote}
                                                    onChange={e => setStatus(r, e.target.value as PalletStatus)}
                                                >
                                                    {statusChoices.map(s => (
                                                        <option key={s} value={s}>{t(`pallets_status_${s}`)}</option>
                                                    ))}
                                                </select>
                                            )}
                                            {duplicatePallets.length > 0 && (
                                                <div className="pallets-admin-status-note" title={duplicateNote} data-testid={`pallet-duplicate-${r.pallet_sku}`}>
                                                    {t("pallets_admin_duplicate_also_on", { pallets: duplicatePallets.join(", ") })}
                                                </div>
                                            )}
                                        </td>
                                        <td
                                            className="pallets-admin-listed-by"
                                            data-testid={`pallet-listed-by-${r.pallet_sku}`}
                                            title={r.status === "listed" ? (r.listed_by_name ?? undefined)
                                                : r.status === "listing" && r.listing_by_name ? t("pallets_admin_listing_by", { name: r.listing_by_name }) : undefined}
                                        >
                                            {r.status === "listed" && r.listed_by_name ? shortName(r.listed_by_name)
                                                : r.status === "listing" && r.listing_by_name ? <span className="pallets-admin-listing-by">{shortName(r.listing_by_name)}</span>
                                                : "—"}
                                        </td>
                                        <td className="pallets-admin-actions-cell">
                                            {!voided && (
                                                <>
                                                    <button type="button" className="btn btn-ghost btn-sm" disabled={busy === r.pallet_sku || readOnly} aria-disabled={readOnly} onClick={() => reprint(r)} aria-label={`${t("pallets_reprint")} ${r.pallet_sku}`} title={readOnly ? t("read_only_mode") : t("pallets_reprint")}>
                                                        <Printer size={14} />
                                                    </button>
                                                    <button type="button" className="btn btn-ghost btn-sm" disabled={busy === r.pallet_sku || readOnly || locked} aria-disabled={readOnly || locked} onClick={() => voidPallet(r)} aria-label={`${t("pallets_admin_void")} ${r.pallet_sku}`} title={readOnly ? t("read_only_mode") : locked ? batchNote : t("pallets_admin_void")}>
                                                        <Trash2 size={14} />
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {total > 0 && (
                <div className="locations-table-footer pallets-admin-footer">
                    <div className="pallets-admin-footer-left">
                        <span>{t("locations_showing_range", {
                            from: String(rangeFrom),
                            to: String(rangeTo),
                            total: String(total),
                        })}</span>
                        <select
                            className="locations-table-search pallets-admin-page-size"
                            value={String(pageSize)}
                            onChange={e => changePageSize(Number(e.target.value))}
                            aria-label={t("logs_page_size")}
                            title={t("logs_page_size")}
                        >
                            {PAGE_SIZES.map(n => (
                                <option key={n} value={String(n)}>
                                    {t("logs_page_size_option", { count: String(n) })}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="locations-table-pagination">
                        <button
                            type="button"
                            className="locations-table-page-btn"
                            onClick={() => setPage(p => Math.max(0, p - 1))}
                            disabled={safePage === 0 || loading}
                            aria-label={t("pagination_previous")}
                        >
                            <ChevronLeft size={16} />
                            <span>{t("pagination_previous")}</span>
                        </button>
                        <span className="locations-table-page-indicator">
                            {t("pagination_page_x_of_y", {
                                page: String(safePage + 1),
                                total: String(totalPages),
                            })}
                        </span>
                        <button
                            type="button"
                            className="locations-table-page-btn"
                            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                            disabled={safePage >= totalPages - 1 || loading}
                            aria-label={t("pagination_next")}
                        >
                            <span>{t("pagination_next")}</span>
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            )}

            {noteTip && (
                <div
                    className="pallets-note-tip"
                    role="tooltip"
                    style={{ top: noteTip.top, left: noteTip.left, maxWidth: noteTip.maxWidth }}
                >
                    {noteTip.text}
                </div>
            )}

            {supplierEdit && (
                <div
                    ref={supplierPopRef}
                    className="pallets-admin-supplier-pop"
                    role="dialog"
                    aria-label={`${t("pallets_admin_change_supplier")} ${supplierEdit.sku}`}
                    style={{ top: supplierEdit.top, left: supplierEdit.left }}
                >
                    <div className="pallets-admin-supplier-pop-title">{t("pallets_admin_change_supplier")} · {supplierEdit.sku}</div>
                    <select
                        className="pallets-admin-supplier-pop-select"
                        value={supplierEdit.value}
                        aria-label={t("pallets_supplier")}
                        autoFocus
                        disabled={supplierSaving}
                        onChange={e => setSupplierEdit(s => (s ? { ...s, value: e.target.value } : s))}
                    >
                        {supplierChoices.map(name => (
                            <option key={name} value={name}>{name}</option>
                        ))}
                    </select>
                    <div className="pallets-admin-supplier-pop-actions">
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSupplierEdit(null)} disabled={supplierSaving}>
                            {t("cancel")}
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={saveSupplier}
                            disabled={supplierSaving || supplierEdit.value.trim() === "" || supplierEdit.value === supplierEdit.current}
                        >
                            {supplierSaving ? t("saving") : t("save")}
                        </button>
                    </div>
                </div>
            )}

            {notesEdit && (
                <div
                    ref={notesPopRef}
                    className="pallets-admin-supplier-pop pallets-admin-supplier-pop--notes"
                    role="dialog"
                    aria-label={`${t("pallets_admin_edit_notes")} ${notesEdit.sku}`}
                    style={{ top: notesEdit.top, left: notesEdit.left }}
                >
                    <div className="pallets-admin-supplier-pop-title">{t("pallets_admin_edit_notes")} · {notesEdit.sku}</div>
                    <textarea
                        className="pallets-admin-notes-pop-text"
                        value={notesEdit.value}
                        aria-label={t("pallets_admin_col_notes")}
                        placeholder={t("pallets_admin_notes_placeholder")}
                        autoFocus
                        rows={4}
                        maxLength={1000}
                        disabled={notesSaving}
                        onChange={e => setNotesEdit(s => (s ? { ...s, value: e.target.value } : s))}
                    />
                    <div className="pallets-admin-supplier-pop-actions">
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setNotesEdit(null)} disabled={notesSaving}>
                            {t("cancel")}
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={saveNotes}
                            disabled={notesSaving || notesEdit.value.trim() === notesEdit.current.trim()}
                        >
                            {notesSaving ? t("saving") : t("save")}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PalletsAdmin;
